// Resolution of the document's `classification` group into something renderable.
//
// The taxonomy has THREE independent levels — asset_type › subcategory › type —
// each with its own confidence. asset_type is always present; subcategory and type
// can be empty, and an empty level means "the evidence doesn't reach that far", not
// "unknown". Nothing here ever substitutes "Unknown" for an absent level.
//
// The icon carries the classification on its own: the most specific level present
// picks the shape, and nothing here assigns a colour. Icons render in the neutral
// foreground colour like every other icon in the app — colour in this UI is
// reserved for severity and risk, and spending it on a taxonomy would make an
// ordinary workstation look as loud as a critical finding.
//
// Every combination the taxonomy defines is mapped below, including the 15 that no
// passive signal produces today. They exist in the taxonomy and will start
// arriving; mapping them now means the day they do, they render correctly instead
// of silently degrading to a family-level fallback.

import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import Box from "@mui/material/Box";
import LaptopMacIcon from "@mui/icons-material/LaptopMac";
import DnsIcon from "@mui/icons-material/Dns";
import AppsIcon from "@mui/icons-material/Apps";
import StorageIcon from "@mui/icons-material/Storage";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import LayersIcon from "@mui/icons-material/Layers";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PrintIcon from "@mui/icons-material/Print";
import RouterIcon from "@mui/icons-material/Router";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import HubIcon from "@mui/icons-material/Hub";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import WifiIcon from "@mui/icons-material/Wifi";
import BalanceIcon from "@mui/icons-material/Balance";
import PublicIcon from "@mui/icons-material/Public";
import MemoryIcon from "@mui/icons-material/Memory";
import SettingsRemoteIcon from "@mui/icons-material/SettingsRemote";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import BuildIcon from "@mui/icons-material/Build";
import ShieldIcon from "@mui/icons-material/Shield";
import HistoryIcon from "@mui/icons-material/History";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import BadgeIcon from "@mui/icons-material/Badge";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import TabletIcon from "@mui/icons-material/Tablet";
import VideocamIcon from "@mui/icons-material/Videocam";
import DvrIcon from "@mui/icons-material/Dvr";
import SensorsIcon from "@mui/icons-material/Sensors";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import HomeIcon from "@mui/icons-material/Home";
import RadarIcon from "@mui/icons-material/Radar";
import WavesIcon from "@mui/icons-material/Waves";
import GradientIcon from "@mui/icons-material/Gradient";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import BiotechIcon from "@mui/icons-material/Biotech";
import ScienceIcon from "@mui/icons-material/Science";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import ComputerIcon from "@mui/icons-material/Computer";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";

const UnclassifiedAssetIcon = ({ sx, style, ...props }) => (
  <Box
    component="span"
    sx={{ position: "relative", display: "inline-flex", fontSize: "1em", ...sx }}
    style={style}
    {...props}
  >
    <ComputerIcon sx={{ fontSize: "inherit", color: "inherit" }} />
    <QuestionMarkIcon
      sx={{
        position: "absolute",
        top: "-0.18em",
        right: "-0.16em",
        fontSize: "0.62em",
        color: "inherit",
        backgroundColor: "#1c1c1c",
        borderRadius: "50%",
      }}
    />
  </Box>
);

// ── asset_type → label ───────────────────────────────────────────────────────
// `unclassified` gets a real label because it is a real taxonomy value ("we looked
// and could not place it"), not a missing one; an empty asset_type — a document
// predating the field — is the one that renders as a dash.
export const ASSET_TYPE_LABELS = {
  it:           "IT",
  ot:           "OT",
  iot:          "IoT",
  iomt:         "IoMT",
  network:      "Network",
  unclassified: "Unclassified",
};

export const assetTypeLabel = (assetType) =>
  ASSET_TYPE_LABELS[String(assetType ?? "").toLowerCase()] ?? "—";

// Orion canonical taxonomy. Deployment is intentionally absent here: it is an
// orthogonal AssetRecord field and must never affect the classification path.
const ICONS = {
  it: DesktopWindowsIcon,
  "it/server": DnsIcon,
  "it/server/database_server": StorageIcon,
  "it/server/domain_controller": ShieldIcon,
  "it/server/dns_server": PublicIcon,
  "it/server/nas": Inventory2Icon,
  "it/server/mail_server": AppsIcon,
  "it/server/web_server": AppsIcon,
  "it/server/hypervisor": LayersIcon,
  "it/server/application_server": AppsIcon,
  "it/endpoint": DesktopWindowsIcon,
  "it/endpoint/workstation": DesktopWindowsIcon,
  "it/endpoint/laptop": LaptopMacIcon,
  "it/endpoint/printer": PrintIcon,
  "ot": PrecisionManufacturingIcon,
  "ot/industrial_control": MemoryIcon,
  "ot/industrial_control/plc": MemoryIcon,
  "ot/industrial_control/rtu": SettingsRemoteIcon,
  "ot/industrial_control/ied": ElectricBoltIcon,
  "ot/industrial_control/dcs": PrecisionManufacturingIcon,
  "ot/industrial_control/safety_controller": ShieldIcon,
  "ot/hmi": TouchAppIcon,
  "ot/historian": HistoryIcon,
  "ot/engineering_workstation": BuildIcon,
  "ot/field_device": SensorsIcon,
  "ot/field_device/sensor": SensorsIcon,
  "ot/field_device/actuator": PrecisionManufacturingIcon,
  "iot": SensorsIcon,
  "iot/mobile": SmartphoneIcon,
  "iot/mobile/smartphone": SmartphoneIcon,
  "iot/mobile/tablet": TabletIcon,
  "iot/mobile/wearable": SmartphoneIcon,
  "iot/voip": PhoneInTalkIcon,
  "iot/voip/ip_phone": PhoneInTalkIcon,
  "iot/voip/conference_system": PhoneInTalkIcon,
  "iot/surveillance": VideocamIcon,
  "iot/surveillance/ip_camera": VideocamIcon,
  "iot/surveillance/nvr": DvrIcon,
  "iot/surveillance/access_control": BadgeIcon,
  "iot/building_automation": HomeWorkIcon,
  "iot/building_automation/bacnet_device": HomeWorkIcon,
  "iot/building_automation/knx_device": HomeWorkIcon,
  "iot/building_automation/hvac_controller": AcUnitIcon,
  "iot/smart_home": HomeIcon,
  "iot/smart_home/smart_speaker": HomeIcon,
  "iot/smart_home/smart_tv": HomeIcon,
  "iot/smart_home/smart_appliance": HomeIcon,
  "iot/media": HomeIcon,
  "iot/media/streaming_stick": HomeIcon,
  "iot/media/game_console": HomeIcon,
  "iomt": LocalHospitalIcon,
  "iomt/medical_imaging": RadarIcon,
  "iomt/medical_imaging/ct_scanner": RadarIcon,
  "iomt/medical_imaging/mri": GradientIcon,
  "iomt/medical_imaging/x_ray": WavesIcon,
  "iomt/medical_imaging/ultrasound": WavesIcon,
  "iomt/medical_imaging/pet_scanner": RadarIcon,
  "iomt/medical_imaging/mammograph": RadarIcon,
  "iomt/clinical_information": FolderSharedIcon,
  "iomt/clinical_information/ris": FolderSharedIcon,
  "iomt/clinical_information/pacs": FolderSharedIcon,
  "iomt/clinical_information/his": FolderSharedIcon,
  "iomt/clinical_information/interface_engine": SwapHorizIcon,
  "iomt/patient_monitoring": MonitorHeartIcon,
  "iomt/patient_monitoring/bedside_monitor": MonitorHeartIcon,
  "iomt/patient_monitoring/central_station": MonitorHeartIcon,
  "iomt/laboratory": ScienceIcon,
  "iomt/laboratory/analyzer": BiotechIcon,
  "iomt/laboratory/lis": ScienceIcon,
  network: RouterIcon,
  "network/layer2": HubIcon,
  "network/layer2/switch": HubIcon,
  "network/layer2/access_point": WifiIcon,
  "network/layer2/hub": HubIcon,
  "network/layer3": RouterIcon,
  "network/layer3/router": RouterIcon,
  "network/layer3/gateway": DeviceHubIcon,
  "network/layer3/firewall": ShieldIcon,
  "network/layer3/load_balancer": BalanceIcon,
  "network/wireless": WifiIcon,
  "network/wireless/wireless_controller": WifiIcon,
  "network/wireless/mesh_node": WifiIcon,
  unclassified: UnclassifiedAssetIcon,
};

// Every "asset_type[/subcategory[/type]]" path the canonical taxonomy defines
// (the ICONS map's own keys) — exported so a classification picker can build
// its tree straight from the same source of truth this file already
// maintains, instead of a second hand-written list drifting out of sync with
// it (see EditAssetDialog.jsx's former CLASSIFICATION_PRESETS, ~50 entries
// hand-picked from this same taxonomy).
export const TAXONOMY_PATHS = Object.keys(ICONS).filter((k) => k !== "unclassified");

const DEPLOYMENT_ICONS = {
  physical: ComputerIcon,
  virtual_machine: ViewInArIcon,
  container: DeveloperBoardIcon,
  hypervisor_host: LayersIcon,
};

// Words the taxonomy uses as acronyms — humanize() would otherwise render them
// Title Case ("Plc", "Mri", "Dns"), which reads as a typo to anyone who knows the
// domain.
const ACRONYMS = new Set([
  "it", "ot", "iot", "iomt", "plc", "rtu", "hmi", "scada", "nas", "san", "dns",
  "dhcp", "vpn", "vfd", "ied", "io", "ct", "mri", "ecg", "ip", "nvr", "dvr",
  "hvac", "bacnet", "pbx", "vlan", "snmp", "ja4", "os", "cve", "ups", "pdu",
  "hl7", "ae", "sni", "cn", "tls", "mdns", "llmnr", "lldp", "cdp", "cldap",
  "ntlm", "netbios", "smb", "ssh", "tcp", "udp", "http", "mtu", "ttl", "mss",
]);

// snake_case taxonomy value → human label. `x_ray` is the one value where the
// underscore is a hyphen in prose, not a word break.
export const humanizeTaxonomy = (value) => {
  if (!value) return "";
  const raw = String(value);
  if (raw === "x_ray") return "X-Ray";
  return raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => (ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
};

const levelOf = (node) => {
  const value = typeof node === "string" ? node : node?.value;
  const trimmed = String(value ?? "").trim();
  return trimmed === "" ? null : trimmed.toLowerCase();
};

const confidenceOf = (node) => {
  const c = typeof node === "object" && node !== null ? node.confidence : null;
  return typeof c === "number" ? c : null;
};

// `classification.types[]` is an ADDITIVE array, not a single value — the canonical
// spec (`asset-classification-task/docs/architecture/
// TASK_SPEC_ESTRAZIONE_NORMALIZZAZIONE_RULES.md` § "Specifica canonica
// classification") is explicit that a device can carry more than one role at once
// (its own worked example is a host that is both a domain controller AND a DNS
// server). Each entry carries its own confidence and source — there is no
// top-level `classification.source[]` any more (verified: zero rules in the whole
// ruleset ever write one), so per-candidate source is the only provenance left for
// this level. Ranked by confidence, highest first; that top entry is "the type" for
// the icon/breadcrumb, and the rest render as secondary chips rather than being
// dropped — a domain controller that is also a DNS server is worth showing as both.
const typesOf = (classification) => {
  const arr = Array.isArray(classification?.types) ? classification.types : [];
  return arr
    .map((t) => ({ value: levelOf(t), confidence: confidenceOf(t), source: (typeof t === "object" && t?.source) || null }))
    .filter((t) => t.value)
    .sort((a, b) => (b.confidence ?? -1) - (a.confidence ?? -1));
};

/**
 * Turns a `classification` group into everything a renderer needs.
 *
 * Returned `levels` only ever contains the levels the document actually carries —
 * an absent subcategory/type is left out rather than filled with a placeholder,
 * because "the evidence stops here" and "unknown" are different statements.
 */
export const resolveClassification = (classification) => {
  const assetType = levelOf(classification?.asset_type) ?? "";
  const subcategory = levelOf(classification?.subcategory);
  const typeCandidates = typesOf(classification);
  const type = typeCandidates[0]?.value ?? null;
  const deployment = levelOf(classification?.deployment);

  const levels = [
    { key: "asset_type", value: assetType, confidence: confidenceOf(classification?.asset_type) },
    subcategory ? { key: "subcategory", value: subcategory, confidence: confidenceOf(classification?.subcategory) } : null,
    type ? { key: "type", value: type, confidence: typeCandidates[0].confidence, source: typeCandidates[0].source } : null,
  ].filter((l) => l && l.value);

  // Most specific level present wins for both the icon and the label — that is the
  // whole point of the three-level shape.
  const iconKey =
    (type && ICONS[`${assetType}/${subcategory}/${type}`] && `${assetType}/${subcategory}/${type}`) ||
    (subcategory && ICONS[`${assetType}/${subcategory}`] && `${assetType}/${subcategory}`) ||
    (ICONS[assetType] && assetType) ||
    null;

  const mostSpecific = type || subcategory || assetType;

  return {
    assetType,
    subcategory,
    type,
    // Every type candidate the rules matched, ranked — `type` above is just
    // `typeCandidates[0].value`. Additional roles beyond the first are what
    // ClassificationSection renders as "Also matches".
    typeCandidates,
    levels,
    // Kept for any caller still reading the pre-`types[]` shape; always empty
    // today (no rule writes a top-level classification.source any more — see
    // typesOf above), so nothing should rely on this for provenance any longer.
    source: classification?.source ?? [],
    // Breadcrumb of the levels that exist, e.g. "IoMT › Medical Imaging › CT Scanner"
    path: levels.map((l) => humanizeTaxonomy(l.value)).join(" › "),
    label: mostSpecific ? humanizeTaxonomy(mostSpecific) : "",
    familyLabel: assetTypeLabel(assetType),
    deployment,
    deploymentIcon: DEPLOYMENT_ICONS[deployment] ?? null,
    icon: ICONS[iconKey] ?? ICONS.unclassified,
  };
};

// Convenience for grid sorting/grouping: a stable key for the resolved combination.
export const classificationKey = (classification) => {
  const { assetType, subcategory, type } = resolveClassification(classification);
  return [assetType, subcategory, type].filter(Boolean).join("/");
};
